import { useState, useEffect } from "react";
import API from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Link } from "react-router-dom";
import {
  ChartBar, Warning, CheckCircle, ArrowRight, Users, Folder, Briefcase, Truck,
  TrendUp, TrendDown, Minus
} from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const PLAN_COLORS = {
  starter: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  professional: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
  enterprise: "bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300",
  one_time_licence: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300",
};

const RISK_CONFIG = {
  high: { label: "High Risk", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400", icon: Warning },
  medium: { label: "Medium", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400", icon: Minus },
  low: { label: "Low Risk", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400", icon: CheckCircle },
};

const CHART_COLORS = ["#ef4444", "#f59e0b", "#10b981"];

export default function TenantComparisonPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("churn_risk");

  useEffect(() => {
    API.get("/platform/tenant-comparison")
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!data) return <div className="text-center py-20 text-muted-foreground">Failed to load comparison data</div>;

  const { tenants, summary } = data;

  const sortedTenants = [...tenants].sort((a, b) => {
    if (sortBy === "adoption") return b.adoption_score - a.adoption_score;
    if (sortBy === "activity") return b.activity.logins_7d - a.activity.logins_7d;
    if (sortBy === "users") return b.usage.users - a.usage.users;
    // Default: churn risk (high first)
    const riskOrder = { high: 0, medium: 1, low: 2 };
    return riskOrder[a.churn_risk] - riskOrder[b.churn_risk];
  });

  const riskChartData = [
    { name: "High", value: summary.churn_risk.high, fill: "#ef4444" },
    { name: "Medium", value: summary.churn_risk.medium, fill: "#f59e0b" },
    { name: "Low", value: summary.churn_risk.low, fill: "#10b981" },
  ];

  return (
    <div data-testid="tenant-comparison-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight" data-testid="comparison-title">SaaS Tenant Comparison</h1>
          <p className="text-sm text-muted-foreground mt-1">Side-by-side metrics across all SaaS tenants</p>
        </div>
        <Badge variant="outline" className="text-xs">{summary.total} tenants</Badge>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="border border-border rounded-md p-4 bg-card" data-testid="comparison-avg-adoption">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Avg Feature Adoption</p>
          <div className="flex items-center gap-3">
            <p className="text-2xl font-bold font-[Chivo]">{summary.avg_adoption_score}%</p>
            <Progress value={summary.avg_adoption_score} className="flex-1 h-2" />
          </div>
        </div>
        <div className="border border-border rounded-md p-4 bg-card" data-testid="comparison-risk-summary">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Churn Risk Distribution</p>
          <div className="flex items-center gap-3">
            {riskChartData.map((r) => (
              <div key={r.name} className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: r.fill }} />
                <span className="text-xs font-medium">{r.value} {r.name}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="border border-border rounded-md p-4 bg-card" data-testid="comparison-risk-chart">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Risk Breakdown</p>
          <ResponsiveContainer width="100%" height={50}>
            <BarChart data={riskChartData} layout="vertical">
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" hide />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={14}>
                {riskChartData.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Sort Controls */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-xs text-muted-foreground">Sort by:</span>
        {[
          { key: "churn_risk", label: "Churn Risk" },
          { key: "adoption", label: "Feature Adoption" },
          { key: "activity", label: "Activity (7d)" },
          { key: "users", label: "Team Size" },
        ].map((s) => (
          <Button
            key={s.key}
            size="sm"
            variant={sortBy === s.key ? "default" : "outline"}
            className="text-xs h-7"
            onClick={() => setSortBy(s.key)}
            data-testid={`sort-${s.key}`}
          >
            {s.label}
          </Button>
        ))}
      </div>

      {/* Tenant Cards */}
      <div className="space-y-3" data-testid="tenant-comparison-list">
        {sortedTenants.map((t) => {
          const risk = RISK_CONFIG[t.churn_risk] || RISK_CONFIG.low;
          const RiskIcon = risk.icon;
          return (
            <div key={t.id} className="border border-border rounded-md bg-card overflow-hidden hover:border-primary/30 transition-colors" data-testid={`tenant-card-${t.id}`}>
              <div className="flex flex-col lg:flex-row">
                {/* Info Column */}
                <div className="p-4 lg:w-64 border-b lg:border-b-0 lg:border-r border-border shrink-0">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-sm font-bold font-[Chivo] truncate">{t.name}</h3>
                    <Badge className={`text-[8px] ${PLAN_COLORS[t.plan] || PLAN_COLORS.starter}`}>{t.plan?.replace(/_/g, " ")}</Badge>
                  </div>
                  <p className="text-[10px] text-muted-foreground mb-2">@{t.slug}</p>
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold ${risk.cls}`}>
                      <RiskIcon size={10} weight="bold" />
                      {risk.label}
                    </span>
                    {!t.setup_complete && (
                      <Badge variant="outline" className="text-[8px] text-amber-600">Setup Incomplete</Badge>
                    )}
                  </div>
                </div>

                {/* Metrics */}
                <div className="flex-1 p-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                    <div className="text-center">
                      <Users size={14} className="mx-auto text-muted-foreground mb-0.5" />
                      <p className="text-lg font-bold font-mono">{t.usage.users}</p>
                      <p className="text-[9px] text-muted-foreground">Users</p>
                    </div>
                    <div className="text-center">
                      <Folder size={14} className="mx-auto text-muted-foreground mb-0.5" />
                      <p className="text-lg font-bold font-mono">{t.usage.projects}</p>
                      <p className="text-[9px] text-muted-foreground">Projects</p>
                    </div>
                    <div className="text-center">
                      <Briefcase size={14} className="mx-auto text-muted-foreground mb-0.5" />
                      <p className="text-lg font-bold font-mono">{t.usage.jobs}</p>
                      <p className="text-[9px] text-muted-foreground">Jobs</p>
                    </div>
                    <div className="text-center">
                      <Truck size={14} className="mx-auto text-muted-foreground mb-0.5" />
                      <p className="text-lg font-bold font-mono">{t.usage.vendors}</p>
                      <p className="text-[9px] text-muted-foreground">Suppliers</p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-0.5 mb-0.5">
                        {t.activity.logins_7d > 0 ? <TrendUp size={14} className="text-emerald-500" /> : <TrendDown size={14} className="text-red-400" />}
                      </div>
                      <p className="text-lg font-bold font-mono">{t.activity.logins_7d}</p>
                      <p className="text-[9px] text-muted-foreground">Logins (7d)</p>
                    </div>
                    <div className="text-center">
                      <ChartBar size={14} className="mx-auto text-muted-foreground mb-0.5" />
                      <p className="text-lg font-bold font-mono">{t.adoption_score}%</p>
                      <p className="text-[9px] text-muted-foreground">Adoption</p>
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-muted-foreground shrink-0">Feature Adoption</span>
                      <Progress value={t.adoption_score} className="flex-1 h-1.5" />
                      <span className="text-[10px] font-mono font-semibold">{t.adoption_score}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
